import { History, Loader2, Save, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Modal } from '@/components/modal';
import type { PromptVersion } from '@/services/external-chat/types';

const versionLabel = (version: PromptVersion) =>
  version.name || `Version ${version.version}`;

interface InstructionVersionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  versions: PromptVersion[];
  loading: boolean;
  loadFailed: boolean;
  retrying: boolean;
  onRetry: () => void;
  /** True while any write that touches the live instructions is in flight —
   *  another activation, or a config save that would PATCH a pre-activate
   *  draft over it. */
  activateDisabled: boolean;
  activatingId: string | null;
  onRequestActivate: (version: PromptVersion) => void;
  onRequestDelete: (version: PromptVersion) => void;
}

export const InstructionVersionsModal = ({
  open,
  onOpenChange,
  versions,
  loading,
  loadFailed,
  retrying,
  onRetry,
  activateDisabled,
  activatingId,
  onRequestActivate,
  onRequestDelete,
}: InstructionVersionsModalProps) => {
  // A failed load also yields an empty list — say so rather than claiming the
  // user has never saved a version.
  const emptyMessage = loadFailed
    ? "Couldn't load version history."
    : 'No versions saved yet. Save your current instructions as a version to enable rollback.';

  return (
    <Modal
      isDialogOpen={open}
      setIsDialogOpen={onOpenChange}
      title={
        <span className='flex items-center gap-2'>
          <History className='size-5' />
          Instruction Versions
        </span>
      }
      description='Save and recall past agent instructions. Activating a version overwrites the live instructions immediately.'
      contentClassName='sm:max-w-lg'
    >
      <div className='space-y-3'>
        {loading ? (
          <p className='text-sm text-muted-foreground py-4 text-center'>
            <Loader2 className='size-4 animate-spin inline mr-1' />
            Loading versions...
          </p>
        ) : versions.length === 0 ? (
          <div className='space-y-2 py-4 text-center'>
            <p className='text-sm text-muted-foreground'>{emptyMessage}</p>
            {loadFailed && (
              <Button
                variant='outline'
                size='sm'
                onClick={() => onRetry()}
                disabled={retrying}
              >
                {retrying && <Loader2 className='size-3.5 animate-spin mr-1' />}
                Try again
              </Button>
            )}
          </div>
        ) : (
          versions.map((version) => (
            <div
              key={version.prompt_id}
              className='flex items-center justify-between gap-2 rounded-md border px-3 py-2'
            >
              <div className='flex-1 min-w-0'>
                <div className='flex items-center gap-2'>
                  <span className='text-sm font-medium truncate'>
                    {versionLabel(version)}
                  </span>
                  <Badge variant='outline' className='text-xs shrink-0'>
                    v{version.version}
                  </Badge>
                </div>
                <span className='text-xs text-muted-foreground'>
                  {new Date(version.created_at).toLocaleDateString()}
                  {version.created_by ? ` · ${version.created_by}` : ''}
                </span>
              </div>
              <div className='flex items-center gap-1 shrink-0'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => onRequestActivate(version)}
                  disabled={activateDisabled}
                >
                  {activatingId === version.prompt_id && (
                    <Loader2 className='size-3.5 animate-spin mr-1' />
                  )}
                  Activate
                </Button>
                <Button
                  variant='ghost'
                  size='icon'
                  className='size-8'
                  aria-label={`Delete ${versionLabel(version)}`}
                  onClick={() => onRequestDelete(version)}
                >
                  <Trash2 className='size-3.5 text-muted-foreground' />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </Modal>
  );
};

interface SaveInstructionVersionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  onNameChange: (name: string) => void;
  saving: boolean;
  onSave: () => void;
}

export const SaveInstructionVersionModal = ({
  open,
  onOpenChange,
  name,
  onNameChange,
  saving,
  onSave,
}: SaveInstructionVersionModalProps) => (
  <Modal
    isDialogOpen={open}
    setIsDialogOpen={onOpenChange}
    title='Save as version'
    description='Snapshots the current instructions text so you can recall it later.'
    contentClassName='sm:max-w-sm'
    footer={
      <Button onClick={onSave} disabled={saving}>
        {saving && <Loader2 className='size-4 animate-spin mr-1' />}
        <Save className='size-4 mr-1' />
        Save version
      </Button>
    }
  >
    <div className='space-y-2'>
      <Label htmlFor='version-name' className='text-xs font-semibold'>
        Name (optional)
      </Label>
      <Input
        id='version-name'
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        placeholder='e.g. Friendly tone, no emoji'
        maxLength={100}
        className='text-sm'
      />
      <p className='text-xs text-muted-foreground'>
        Leave blank to label it &quot;Version N&quot;. Max 100 characters.
      </p>
    </div>
  </Modal>
);

interface DeleteVersionModalProps {
  version: PromptVersion | null;
  onCancel: () => void;
  onConfirm: (version: PromptVersion) => void;
}

export const DeleteVersionModal = ({
  version,
  onCancel,
  onConfirm,
}: DeleteVersionModalProps) => (
  <Modal
    isDialogOpen={!!version}
    setIsDialogOpen={(open) => {
      if (!open) onCancel();
    }}
    title='Delete version'
    description={
      version
        ? `This action cannot be undone. "${versionLabel(version)}" will be permanently deleted.`
        : undefined
    }
    contentClassName='sm:max-w-sm'
    footer={
      <div className='flex gap-2'>
        <Button variant='outline' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant='destructive'
          onClick={() => {
            if (version) onConfirm(version);
          }}
        >
          Delete version
        </Button>
      </div>
    }
  />
);

interface ActivateVersionModalProps {
  version: PromptVersion | null;
  onCancel: () => void;
  onConfirm: (version: PromptVersion) => void;
}

/** Confirmation shown only when the instructions editor has unsaved edits at
 * the moment the user clicks Activate — activation writes straight to the
 * server's `custom_instructions`, so an unsaved draft would otherwise be
 * silently discarded with no warning. */
export const ActivateVersionModal = ({
  version,
  onCancel,
  onConfirm,
}: ActivateVersionModalProps) => (
  <Modal
    isDialogOpen={!!version}
    setIsDialogOpen={(open) => {
      if (!open) onCancel();
    }}
    title='Activate version'
    description={
      version
        ? `You have unsaved edits in the instructions editor. Activating "${versionLabel(version)}" overwrites the live instructions on the server immediately, replacing your unsaved draft.`
        : undefined
    }
    contentClassName='sm:max-w-sm'
    footer={
      <div className='flex gap-2'>
        <Button variant='outline' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (version) onConfirm(version);
          }}
        >
          Activate anyway
        </Button>
      </div>
    }
  />
);
